import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import { DateRangePicker } from "rsuite"
import Chart from "@/components/charts/Chart"
import { useEffect, useState } from "react"
import { apiFetch } from "@/hooks/apiFetch"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { useAppSelector } from "@/lib/hooks"
import { BrandSelect } from "../../Brand/BrandSelect"
import { useReportBranch } from "@/components/Reports/ReportBranchFilter"

// Color palette for tags
const TAG_COLORS = [
  "#3f51b5",
  "#4caf50",
  "#ff9800",
  "#e91e63",
  "#00bcd4",
  "#8bc34a",
  "#ff5722",
  "#9c27b0",
  "#607d8b",
  "#795548",
  "#2196f3",
  "#009688",
  "#ffeb3b",
  "#673ab7",
  "#f44336",
]

const TagSalesReport = () => {
  const [tagData, setTagData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    new Date(),
  ])
  const [limit, setLimit] = useState(10)
  const [sortBy, setSortBy] = useState("total_sales")
  const [sortDirection, setSortDirection] = useState("desc")
  const { branchId } = useReportBranch()

  // export Daily Report
  const handleExport = async () => {
    return exportReport({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      reportType: "tag-sales-report",
      limit: limit.toString(),
      sort_by: sortBy,
      sort_direction: sortDirection,
      brand_id: selectedBrandId,
      branchId,
    })
  }

  const selectedBrandId = useAppSelector((state) => state.brand.selectedBrandId)
  useEffect(() => {
    const fetchTagData = async () => {
      try {
        setLoading(true)
        setError(null)

        const params = new URLSearchParams({
          start_date: dateRange[0].toISOString(),
          end_date: dateRange[1].toISOString(),
          limit: limit.toString(),
          sort_by: sortBy,
          sort_direction: sortDirection,
          brand_id: selectedBrandId,
        })

        if (branchId) {
          params.set("branch_id", branchId.toString())
        }

        const data = await apiFetch(`report/sales-by-tag?${params.toString()}`)
        setTagData(data || [])
      } catch (err) {
        console.error(err)
        setError("Failed to fetch tag sales data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchTagData()
  }, [dateRange, limit, sortBy, sortDirection, selectedBrandId, branchId])

  // Prepare chart data with distinct colors
  const chartData =
    tagData.length > 0
      ? {
          labels: tagData.map((item) => item.tag_name),
          salesData: tagData.map((item) => parseFloat(item.total_sales)),
          quantityData: tagData.map((item) =>
            parseInt(item.total_quantity_sold)
          ),
          // Use tag_color if available, otherwise cycle through our color palette
          colors: tagData.map(
            (item, index) =>
              item.tag_color || TAG_COLORS[index % TAG_COLORS.length]
          ),
          percentages: tagData.map((item) =>
            parseFloat(item.sales_percentage || 0)
          ),
        }
      : null

  return (
    <Box sx={{ p: 3 }}>
      <div className="flex justify-between">
        <Typography variant="h6" gutterBottom>
          <div> Sales by Tag Report</div>
        </Typography>
        <div className="w-[300px]">
          <BrandSelect />
        </div>
      </div>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption">Date Range</Typography>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format="MMM dd, yyyy"
                placeholder="Select date range"
                cleanable={false}
                style={{ width: "100%" }}
                appearance="default"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Max Tags</InputLabel>
                <Select
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  label="Max Tags"
                >
                  <MenuItem value={5}>Top 5</MenuItem>
                  <MenuItem value={10}>Top 10</MenuItem>
                  <MenuItem value={20}>Top 20</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="total_sales">Total Sales</MenuItem>
                  <MenuItem value="total_quantity_sold">Units Sold</MenuItem>
                  <MenuItem value="total_products">Product Count</MenuItem>
                  <MenuItem value="total_orders">Order Count</MenuItem>
                  <MenuItem value="avg_order_value">Avg Order Value</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort Direction</InputLabel>
                <Select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  label="Sort Direction"
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="outlined"
                onClick={() => handleExport()}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "7px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "6px 3px",
                  marginTop: "10px",
                }}
                color="primary"
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {/* Summary Cards */}
      {!loading && !error && tagData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Tags</Typography>
              <Typography variant="h5" color="primary">
                {tagData.length}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Sales</Typography>
              <Typography variant="h5" color="secondary">
                Rs.{" "}
                {tagData
                  .reduce((sum, item) => sum + parseFloat(item.total_sales), 0)
                  .toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Top Performing Tag</Typography>
              <Typography>
                {tagData[0]?.tag_name} (Rs.{" "}
                {parseFloat(tagData[0]?.total_sales || 0).toLocaleString(
                  "en-IN",
                  { maximumFractionDigits: 2 }
                )}
                )
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      {chartData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sales by Tag
                </Typography>

                <div className="h-[350px]">
                  <Chart
                    type="doughnut"
                    data={chartData.salesData}
                    labels={chartData.labels}
                    title="Sales Distribution"
                    backgroundColor={chartData.colors}
                    borderColor={chartData.colors} // Use same colors for border
                    tooltipLabel="Rs."
                  />
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Units Sold by Tag
                </Typography>
                <Chart
                  type="bar"
                  data={chartData.quantityData}
                  labels={chartData.labels}
                  title="Units Sold"
                  backgroundColor={chartData.colors.map(
                    (color) => `${color}80`
                  )} // Add opacity
                  borderColor={chartData.colors}
                  tooltipLabel="Units"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tag Sales Details
          </Typography>
          <DataTable
            columns={columns}
            data={tagData}
            isLoading={loading}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default TagSalesReport
